import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Home, Users, Plus, Copy, RefreshCw, LogOut, Crown,
  ArrowLeft, Loader2, Receipt, TrendingUp, Settings,
  UserPlus, Trash2, Check, Share2, Target, ShoppingCart,
  UtensilsCrossed, Briefcase, UserCheck, Edit2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const HouseholdPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [newHouseholdName, setNewHouseholdName] = useState("");
  const [newBudget, setNewBudget] = useState("500");
  const [copied, setCopied] = useState(false);
  const [editSubBudgets, setEditSubBudgets] = useState({});

  useEffect(() => {
    fetchHousehold();
  }, [token]);

  const fetchHousehold = async () => {
    try {
      const res = await axios.get(`${API}/household`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHousehold(res.data);
      
      if (res.data) {
        const receiptsRes = await axios.get(`${API}/household/receipts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReceipts(receiptsRes.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createHousehold = async () => {
    try {
      await axios.post(`${API}/household/create`, {
        name: newHouseholdName || `Foyer de ${user?.pseudo}`,
        monthly_budget: parseFloat(newBudget) || 500
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Foyer créé !");
      setShowCreateDialog(false);
      setNewHouseholdName("");
      fetchHousehold();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const joinHousehold = async () => {
    try {
      await axios.post(`${API}/household/join`, {
        invite_code: inviteCode
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Vous avez rejoint le foyer !");
      setShowJoinDialog(false);
      setInviteCode("");
      fetchHousehold();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    }
  };

  const leaveHousehold = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir quitter ce foyer ?")) return;
    
    try {
      await axios.delete(`${API}/household/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vous avez quitté le foyer");
      setHousehold(null);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const regenerateCode = async () => {
    try {
      const res = await axios.post(`${API}/household/regenerate-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHousehold({ ...household, invite_code: res.data.invite_code });
      toast.success("Nouveau code généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const updateHousehold = async () => {
    try {
      await axios.put(`${API}/household`, {
        name: newHouseholdName || household?.name,
        sub_budgets: editSubBudgets
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Foyer mis à jour");
      setShowSettingsDialog(false);
      fetchHousehold();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(household?.invite_code || "");
    setCopied(true);
    toast.success("Code copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Retirer ce membre du foyer ?")) return;
    
    try {
      await axios.delete(`${API}/household/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Membre retiré");
      fetchHousehold();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const isAdmin = household?.members?.find(m => m.user_id === user?.id)?.role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No household - show join/create options
  if (!household) {
    return (
      <div className="min-h-screen pb-24">
        <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-cyan-500/10 px-4 pt-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/profile" className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-charcoal">Mon Foyer</h1>
                <p className="text-muted-foreground text-sm">Gérez votre budget courses en famille</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-8 max-w-md mx-auto space-y-4">
          <Card className="shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-bold mb-2">Pas encore de foyer</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Créez un foyer pour partager le budget courses avec votre famille
              </p>
              
              <div className="space-y-3">
                <Button 
                  className="w-full gap-2" 
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="w-4 h-4" />
                  Créer un foyer
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  onClick={() => setShowJoinDialog(true)}
                >
                  <UserPlus className="w-4 h-4" />
                  Rejoindre avec un code
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un foyer</DialogTitle>
              <DialogDescription>
                Créez un foyer et invitez vos proches à le rejoindre
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom du foyer</Label>
                <Input
                  placeholder={`Foyer de ${user?.pseudo}`}
                  value={newHouseholdName}
                  onChange={(e) => setNewHouseholdName(e.target.value)}
                />
              </div>
              <div>
                <Label>Budget mensuel (€)</Label>
                <Input
                  type="number"
                  placeholder="500"
                  value={newBudget}
                  onChange={(e) => setNewBudget(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
              <Button onClick={createHousehold}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Join Dialog */}
        <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rejoindre un foyer</DialogTitle>
              <DialogDescription>
                Entrez le code d'invitation partagé par un membre du foyer
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Code d'invitation</Label>
              <Input
                placeholder="ABC123"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowJoinDialog(false)}>Annuler</Button>
              <Button onClick={joinHousehold} disabled={inviteCode.length < 6}>Rejoindre</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Has household - show dashboard
  const budgetUsed = ((household.stats?.this_month_spent || 0) / (household.monthly_budget || 500)) * 100;
  const subBudgets = household.sub_budgets || {};
  const pieData = Object.entries(subBudgets)
    .filter(([, v]) => v.budget > 0)
    .map(([key, v]) => ({ name: v.name, value: v.spent || 0, budget: v.budget, color: v.color, key }));
  const totalSubSpent = pieData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-cyan-500/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/profile" className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-charcoal">{household.name}</h1>
                <p className="text-muted-foreground text-sm">{household.members?.length} membre(s)</p>
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="icon" onClick={() => {
                setNewHouseholdName(household.name);
                setNewBudget(household.monthly_budget?.toString());
                setEditSubBudgets(JSON.parse(JSON.stringify(subBudgets)));
                setShowSettingsDialog(true);
              }} data-testid="household-settings-btn">
                <Settings className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-4xl mx-auto space-y-4">
        {/* Budget Card */}
        <Card className="shadow-lg bg-gradient-to-br from-white to-emerald-50/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-600" />
                Budget du mois
              </h3>
              <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                {household.stats?.this_month_count || 0} tickets
              </Badge>
            </div>
            
            <div className="flex items-end justify-between mb-2">
              <span className="text-3xl font-bold text-charcoal">
                {household.stats?.this_month_spent?.toFixed(0) || 0}€
              </span>
              <span className="text-muted-foreground">/ {household.monthly_budget || 500}€</span>
            </div>
            
            <Progress value={Math.min(budgetUsed, 100)} className="h-3" />
            
            <p className="text-xs text-muted-foreground mt-2">
              {budgetUsed < 100 
                ? `Il reste ${((household.monthly_budget || 500) - (household.stats?.this_month_spent || 0)).toFixed(0)}€`
                : "Budget dépassé !"}
            </p>
          </CardContent>
        </Card>

        {/* Sub-Budgets Breakdown */}
        <Card className="shadow-lg" data-testid="sub-budgets-card">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-emerald-600" />
              Répartition des dépenses
            </h3>

            {/* Pie Chart + Legend */}
            <div className="flex items-center gap-4 mb-4">
              <div className="w-36 h-36 flex-shrink-0" style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData.length > 0 && totalSubSpent > 0 ? pieData : [{ name: "Aucune", value: 1, color: "#E5E7EB" }]}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={60}
                      dataKey="value"
                      strokeWidth={2}
                      stroke="#fff"
                    >
                      {(pieData.length > 0 && totalSubSpent > 0 ? pieData : [{ color: "#E5E7EB" }]).map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `${v.toFixed(2)}€`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="flex-1 space-y-2">
                {Object.entries(subBudgets).map(([key, cat]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <span className="flex-1 truncate">{cat.emoji} {cat.name}</span>
                    <span className="font-semibold tabular-nums">{(cat.spent || 0).toFixed(0)}€</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bars per category */}
            <div className="space-y-3">
              {Object.entries(subBudgets).map(([key, cat]) => {
                const pct = cat.budget > 0 ? Math.min((cat.spent || 0) / cat.budget * 100, 100) : 0;
                return (
                  <div key={key} data-testid={`sub-budget-${key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{cat.emoji} {cat.name}</span>
                      <span className="text-xs text-muted-foreground">{(cat.spent || 0).toFixed(0)}€ / {cat.budget}€</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: cat.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Invite Code */}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-blue-600" />
              Inviter des membres
            </h3>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-100 rounded-lg text-center">
                <p className="text-xs text-muted-foreground mb-1">Code d'invitation</p>
                <p className="text-2xl font-mono font-bold tracking-widest">{household.invite_code}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button size="icon" variant="outline" onClick={copyInviteCode} data-testid="copy-invite-btn">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                {isAdmin && (
                  <Button size="icon" variant="outline" onClick={regenerateCode} data-testid="regen-code-btn">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members */}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-600" />
              Membres du foyer
            </h3>
            <div className="space-y-3">
              {household.members?.map((member) => (
                <motion.div 
                  key={member.user_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                  data-testid={`member-${member.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <ChefAvatar user={{ avatar: member.avatar, pseudo: member.pseudo }} size="sm" />
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {member.pseudo}
                        {member.role === "admin" && <Crown className="w-4 h-4 text-amber-500" />}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {member.total_spent?.toFixed(2) || 0}€ dépensés
                      </p>
                    </div>
                  </div>
                  {isAdmin && member.user_id !== user?.id && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeMember(member.user_id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Receipts */}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-amber-600" />
              Derniers tickets du foyer
            </h3>
            {receipts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun ticket partagé pour le moment
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {receipts.slice(0, 10).map((receipt) => {
                  const catInfo = subBudgets[receipt.expense_category];
                  return (
                    <div key={receipt.id} className="flex items-center justify-between p-2 rounded-lg border">
                      <div className="flex items-center gap-2">
                        <ChefAvatar user={{ avatar: receipt.member_avatar, pseudo: receipt.member_pseudo }} size="xs" />
                        <div>
                          <p className="text-sm font-medium">{receipt.store_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {receipt.member_pseudo} • {new Date(receipt.date || receipt.created_at).toLocaleDateString('fr-FR')}
                            {catInfo && <span className="ml-1">{catInfo.emoji}</span>}
                          </p>
                        </div>
                      </div>
                      <span className="font-semibold">{receipt.total?.toFixed(2)}€</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leave Button */}
        <Button variant="outline" className="w-full text-red-500 border-red-200 hover:bg-red-50" onClick={leaveHousehold} data-testid="leave-household-btn">
          <LogOut className="w-4 h-4 mr-2" />
          Quitter le foyer
        </Button>
      </div>

      {/* Settings Dialog with Sub-budgets */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Paramètres du foyer</DialogTitle>
            <DialogDescription>Configurez le nom et les sous-budgets de votre foyer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du foyer</Label>
              <Input
                value={newHouseholdName}
                onChange={(e) => setNewHouseholdName(e.target.value)}
                data-testid="household-name-input"
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Sous-budgets mensuels
              </Label>
              {Object.entries(editSubBudgets).map(([key, cat]) => (
                <div key={key} className="flex items-center gap-2" data-testid={`edit-sub-budget-${key}`}>
                  <span className="text-lg w-8 text-center">{cat.emoji}</span>
                  <span className="flex-1 text-sm font-medium">{cat.name}</span>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      step="10"
                      className="w-24 h-8 text-sm text-right"
                      value={cat.budget}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setEditSubBudgets(prev => ({
                          ...prev,
                          [key]: { ...prev[key], budget: val }
                        }));
                      }}
                    />
                    <span className="text-xs text-muted-foreground">€</span>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t text-sm flex justify-between font-medium">
                <span>Total</span>
                <span>{Object.values(editSubBudgets).reduce((s, c) => s + (c.budget || 0), 0)}€ / mois</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Annuler</Button>
            <Button onClick={updateHousehold} data-testid="save-household-btn">Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HouseholdPage;
